// src/components/PredictiveAnalytics.js

import { useMemo } from 'react';
import {
  Activity,
  AlertTriangle,
  Truck,
  Target,
  CheckCircle
} from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { useTheme } from '../context/ThemeContext';

// Risk level badge
function RiskBadge({ level, colors }) {
  const config = {
    'High': { color: colors.danger, bg: colors.dangerBg },
    'Medium': { color: colors.warning, bg: colors.warningBg },
    'Low': { color: colors.success, bg: colors.successBg },
  };
  const { color, bg } = config[level] || config['Low'];
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.3rem 0.6rem',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      background: bg,
      color: color,
      marginTop: '0.35rem'
    }}>
      {level} Risk
    </span>
  );
}

// Risk factor progress bar
function RiskFactorItem({ label, value, maxValue, color, colors }) {
  const percentage = (value / maxValue) * 100;
  
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '0.35rem',
      }}>
        <span style={{ fontSize: '0.8rem', color: colors.textSecondary }}>{label}</span>
        <span style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
          color: colors.textPrimary,
        }}>
          {value.toFixed(1)}%
        </span>
      </div>
      <div style={{
        height: '6px',
        background: colors.bgElevated,
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${Math.min(percentage, 100)}%`,
          background: color,
          borderRadius: '3px',
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

// At-risk shipment card
function AtRiskShipmentCard({ shipment, riskScore, riskFactors, colors }) {
  const riskLevel = riskScore >= 70 ? 'High' : riskScore >= 40 ? 'Medium' : 'Low';
  const riskColor = riskScore >= 70 ? colors.danger : riskScore >= 40 ? colors.warning : colors.success;
  
  return (
    <div style={{ 
      background: colors.bgElevated, 
      borderRadius: '12px', 
      padding: '1.25rem', 
      marginBottom: '1rem', 
      borderLeft: `4px solid ${riskColor}` 
    }}>
      {/* Header Row */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '1rem',
        gap: '1rem'
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontFamily: "'JetBrains Mono', monospace", 
            fontSize: '0.75rem', 
            color: colors.textMuted, 
            marginBottom: '0.35rem' 
          }}>
            {shipment.id}
          </div>
          <div style={{ 
            fontSize: '1rem', 
            color: colors.textPrimary, 
            fontWeight: 600 
          }}>
            {shipment.productName?.split(' ')[0] || 'Unknown'}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700, 
            fontFamily: "'JetBrains Mono', monospace", 
            color: riskColor,
            lineHeight: 1
          }}>
            {riskScore.toFixed(0)}%
          </div>
          <RiskBadge level={riskLevel} colors={colors} />
        </div>
      </div>
      
      {/* Risk Factors */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '0.5rem'
      }}>
        {riskFactors.map((factor, i) => (
          <span 
            key={i} 
            style={{ 
              background: colors.bgCard, 
              padding: '0.4rem 0.75rem', 
              borderRadius: '6px',
              fontSize: '0.75rem',
              color: colors.textSecondary,
              border: `1px solid ${colors.border}`,
            }}
          >
            {factor}
          </span>
        ))}
      </div>
    </div>
  );
}

// Scatter tooltip
function ScatterTooltip({ active, payload, colors }) {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  
  return (
    <div style={{
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '0.75rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
    }}>
      <div style={{ fontSize: '0.8rem', color: colors.textPrimary, fontWeight: 500, marginBottom: '0.25rem' }}>
        {data.carrierName?.split(' ')[0] || 'Unknown'}
      </div>
      <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
        Transit: {data.transitHours}h
      </div>
      <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
        Risk: {data.riskScore?.toFixed(0)}%
      </div>
    </div>
  );
}

// Section card
function SectionCard({ icon: Icon, title, children, iconColor, colors }) {
  return (
    <div style={{
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <Icon size={18} color={iconColor || colors.primary} />
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '1.25rem' }}>
        {children}
      </div>
    </div>
  );
}

// Main component
function PredictiveAnalytics({ shipments, excursions }) {
  const { colors } = useTheme();
  
  // Calculate risk factors for each carrier
  const carrierRiskFactors = useMemo(() => {
    const carriers = {};
    
    shipments.forEach(s => {
      if (!carriers[s.carrierName]) {
        carriers[s.carrierName] = {
          total: 0,
          excursions: 0,
          critical: 0,
          totalTransitHours: 0,
        };
      }
      carriers[s.carrierName].total++;
      carriers[s.carrierName].totalTransitHours += s.transitHours || 0;
      if (s.hasExcursion) {
        carriers[s.carrierName].excursions++;
        if (s.excursionSeverity === 'Critical') {
          carriers[s.carrierName].critical++;
        }
      }
    });
    
    return Object.entries(carriers).map(([name, data]) => ({
      name: name?.split(' ')[0] || 'Unknown',
      fullName: name,
      excursionRate: (data.excursions / data.total) * 100,
      criticalRate: (data.critical / data.total) * 100,
      avgTransitHours: data.totalTransitHours / data.total,
      shipments: data.total,
    }));
  }, [shipments]);
  
  // Calculate risk factors for each product type
  const productRiskFactors = useMemo(() => {
    const products = {};
    
    shipments.forEach(s => {
      if (!products[s.productType]) {
        products[s.productType] = { total: 0, excursions: 0 };
      }
      products[s.productType].total++;
      if (s.hasExcursion) {
        products[s.productType].excursions++;
      }
    });
    
    return Object.entries(products).map(([name, data]) => ({
      name: name?.replace(' Vaccine', '') || 'Unknown',
      fullName: name,
      excursionRate: (data.excursions / data.total) * 100,
    }));
  }, [shipments]);
  
  // Calculate risk score for in-transit shipments
  const atRiskShipments = useMemo(() => {
    const inTransit = shipments.filter(s => s.status === 'In Transit');
    
    return inTransit.map(s => {
      const carrierFactor = carrierRiskFactors.find(c => c.fullName === s.carrierName);
      const productFactor = productRiskFactors.find(p => p.fullName === s.productType);
      
      let riskScore = 0;
      const riskFactors = [];
      
      // Carrier risk (40% weight)
      const carrierRisk = carrierFactor?.excursionRate || 30;
      riskScore += carrierRisk * 0.4;
      if (carrierRisk > 35) {
        riskFactors.push(`High-risk carrier (${carrierRisk.toFixed(0)}%)`);
      }
      
      // Product risk (30% weight)
      const productRisk = productFactor?.excursionRate || 30;
      riskScore += productRisk * 0.3;
      if (productRisk > 35) {
        riskFactors.push(`Sensitive product`);
      }
      
      // Transit time risk (20% weight)
      const transitHours = s.transitHours || 0;
      const transitRisk = Math.min((transitHours / 72) * 50, 50);
      riskScore += transitRisk * 0.2;
      if (transitHours > 48) {
        riskFactors.push(`Long transit (${transitHours}h)`);
      }
      
      // Temperature range risk (10% weight)
      const tempRange = (s.tempMax || 0) - (s.tempMin || 0);
      const tempRisk = tempRange < 10 ? 40 : tempRange < 20 ? 25 : 10;
      riskScore += tempRisk * 0.1;
      if (tempRange < 10) {
        riskFactors.push(`Narrow temp range`);
      }
      
      // Value risk bonus
      if ((s.totalValue || 0) > 100000) {
        riskFactors.push(`High value ($${((s.totalValue || 0)/1000).toFixed(0)}K)`);
      }
      
      return {
        ...s,
        riskScore: Math.min(riskScore, 100),
        riskFactors,
        carrierExcursionRate: carrierRisk,
        transitHours: s.transitHours || 0,
      };
    }).sort((a, b) => b.riskScore - a.riskScore);
  }, [shipments, carrierRiskFactors, productRiskFactors]);
  
  // Scatter plot data
  const scatterData = useMemo(() => {
    return atRiskShipments.map(s => ({
      transitHours: s.transitHours,
      riskScore: s.riskScore,
      carrierName: s.carrierName,
      id: s.id,
    }));
  }, [atRiskShipments]);
  
  // Overall risk metrics
  const riskMetrics = useMemo(() => {
    const highRisk = atRiskShipments.filter(s => s.riskScore >= 70).length;
    const mediumRisk = atRiskShipments.filter(s => s.riskScore >= 40 && s.riskScore < 70).length;
    const lowRisk = atRiskShipments.filter(s => s.riskScore < 40).length;
    const avgRisk = atRiskShipments.length > 0 
      ? atRiskShipments.reduce((sum, s) => sum + s.riskScore, 0) / atRiskShipments.length 
      : 0;
    
    return { highRisk, mediumRisk, lowRisk, avgRisk, total: atRiskShipments.length };
  }, [atRiskShipments]);
  
  // Radar chart data
  const radarData = useMemo(() => {
    if (carrierRiskFactors.length === 0) return [];
    
    const avgCarrierRisk = carrierRiskFactors.reduce((sum, c) => sum + c.excursionRate, 0) / carrierRiskFactors.length;
    const avgProductRisk = productRiskFactors.reduce((sum, p) => sum + p.excursionRate, 0) / (productRiskFactors.length || 1);
    const avgTransitTime = carrierRiskFactors.reduce((sum, c) => sum + c.avgTransitHours, 0) / carrierRiskFactors.length;
    const avgCriticalRate = carrierRiskFactors.reduce((sum, c) => sum + c.criticalRate, 0) / carrierRiskFactors.length;
    
    return [
      { factor: 'Carrier Risk', value: avgCarrierRisk, fullMark: 50 },
      { factor: 'Product Sensitivity', value: avgProductRisk, fullMark: 50 },
      { factor: 'Transit Time', value: (avgTransitTime / 72) * 50, fullMark: 50 },
      { factor: 'Critical Rate', value: avgCriticalRate * 2, fullMark: 50 },
      { factor: 'Volume', value: Math.min(shipments.length / 20, 50), fullMark: 50 },
    ];
  }, [carrierRiskFactors, productRiskFactors, shipments]);

  const styles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    
    statCard: {
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '1.25rem',
      textAlign: 'center',
    },
    
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      fontFamily: "'JetBrains Mono', monospace",
      marginBottom: '0.25rem',
    },
    
    statLabel: {
      fontSize: '0.8rem',
      color: colors.textMuted,
    },
    
    twoColumn: {
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    
    chartContainer: {
      height: '280px',
    },
  };

  return (
    <div>
      {/* Risk Overview Stats */}
      <div style={styles.grid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: colors.danger }}>
            {riskMetrics.highRisk}
          </div>
          <div style={styles.statLabel}>High Risk Shipments</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: colors.warning }}>
            {riskMetrics.mediumRisk}
          </div>
          <div style={styles.statLabel}>Medium Risk</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: colors.success }}>
            {riskMetrics.lowRisk}
          </div>
          <div style={styles.statLabel}>Low Risk</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {riskMetrics.avgRisk.toFixed(0)}%
          </div>
          <div style={styles.statLabel}>Avg Risk Score</div>
        </div>
      </div>
      
      {/* Two Column Layout */}
      <div style={styles.twoColumn}>
        {/* At-Risk Shipments */}
        <SectionCard icon={AlertTriangle} title="At-Risk In-Transit Shipments" iconColor={colors.danger} colors={colors}>
          <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '0.5rem' }}>
            {atRiskShipments.slice(0, 6).map(s => (
              <AtRiskShipmentCard
                key={s.id}
                shipment={s}
                riskScore={s.riskScore}
                riskFactors={s.riskFactors}
                colors={colors}
              />
            ))}
            {atRiskShipments.length === 0 && (
              <div style={{ textAlign: 'center', color: colors.textMuted, padding: '2rem' }}>
                <CheckCircle size={32} color={colors.success} style={{ marginBottom: '0.5rem' }} />
                <div>No in-transit shipments at high risk</div>
              </div>
            )}
          </div>
        </SectionCard>
        
        {/* Risk Factors Radar */}
        <SectionCard icon={Target} title="Overall Risk Profile" iconColor={colors.purple} colors={colors}>
          <div style={styles.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke={colors.border} />
                <PolarAngleAxis 
                  dataKey="factor" 
                  stroke={colors.textMuted}
                  fontSize={10}
                  tick={{ fill: colors.textMuted }}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 50]} 
                  stroke={colors.textMuted}
                  fontSize={9}
                  tick={{ fill: colors.textMuted }}
                />
                <Radar
                  name="Risk"
                  dataKey="value"
                  stroke={colors.primary}
                  fill={colors.primary}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>
      
      {/* Risk vs Transit Time Scatter */}
      <SectionCard icon={Activity} title="Risk Score vs Transit Time" iconColor={colors.info} colors={colors}>
        <div style={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <XAxis 
                dataKey="transitHours" 
                name="Transit Hours"
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: colors.border }}
                label={{ value: 'Transit Hours', position: 'bottom', fill: colors.textMuted, fontSize: 11, offset: -5 }}
              />
              <YAxis 
                dataKey="riskScore" 
                name="Risk Score"
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: colors.border }}
                label={{ value: 'Risk %', angle: -90, position: 'insideLeft', fill: colors.textMuted, fontSize: 11 }}
                domain={[0, 100]}
              />
              <Tooltip content={<ScatterTooltip colors={colors} />} />
              <Scatter data={scatterData} fill={colors.primary}>
                {scatterData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={
                      entry.riskScore >= 70 ? colors.danger :
                      entry.riskScore >= 40 ? colors.warning : colors.success
                    }
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
      
      {/* Carrier Risk Breakdown */}
      <div style={{ marginTop: '1.5rem' }}>
        <SectionCard icon={Truck} title="Carrier Risk Analysis" iconColor={colors.warning} colors={colors}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {carrierRiskFactors.map(carrier => (
              <div key={carrier.fullName}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: colors.textPrimary }}>
                    {carrier.name}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                    {carrier.shipments} shipments
                  </span>
                </div>
                <RiskFactorItem
                  label="Excursion Rate"
                  value={carrier.excursionRate}
                  maxValue={50}
                  color={carrier.excursionRate > 35 ? colors.danger : carrier.excursionRate > 25 ? colors.warning : colors.success}
                  colors={colors}
                />
                <RiskFactorItem
                  label="Critical Rate"
                  value={carrier.criticalRate}
                  maxValue={20}
                  color={carrier.criticalRate > 10 ? colors.danger : carrier.criticalRate > 5 ? colors.warning : colors.success}
                  colors={colors}
                />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export default PredictiveAnalytics;